import { useSelector } from 'react-redux';
import React, { ReactNode, useCallback } from 'react';
import Menu from 'js/components/elements/menu/Menu';
import { ReactComponent as StarIcon } from 'new/components/icons/star.svg';
import { ReactComponent as KeyIcon } from 'new/components/icons/key.svg';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import { ReactComponent as DetailsIcon } from 'new/components/icons/fi-rr-layers.svg';
import { Project } from 'new/entities/Project';
import { PROJECT_DETAIL_VIEW, PROJECT_DETAIL_VIEW_ACCESS_TAB } from 'js/constants/projects';
import { deleteProject } from 'js/actions';
import { makeIsProcessGoingOn } from 'js/reducers/processes/selectors';
import { DELETE_PROJECT } from 'js/constants/processTypes';
import { DISABLE_DEMO_FEATURES } from 'js/constants/config';
import useAppDispatch from 'js/hooks/useAppDispatch';

type Props = {
  data: Project;
  horizontalDots?: boolean;
  isDetected?: boolean;
  includeDetailsOption?: boolean;
  onDetailClicked?: (data: Project, type: string) => void;
};

const isDeletingSelector = makeIsProcessGoingOn(DELETE_PROJECT);

const ProjectDetailsActions = ({
  data,
  horizontalDots,
  onDetailClicked,
  includeDetailsOption = true,
}: Props) => {
  const isDeleting = useSelector(isDeletingSelector);
  const dispatch = useAppDispatch();
  const handleDelete = useCallback(() => {
    dispatch(deleteProject(data.id));
  }, [data]);

  const isOwner = data?.access_roles?.includes?.('owner') || data?.access_roles === 'owner';
  const isAdmin =
    isOwner || data?.access_roles?.includes?.('admin') || data?.access_roles === 'admin';
  const menuItems = [
    {
      label: 'Project actions',
      type: 'heading',
    },
  ] as {
    label: string;
    icon?: ReactNode;
    color?: string;
    onClick?: () => void;
    type?: string;
    disable?: boolean;
    dialogMessage?: string;
    primaryButtonColor?: string;
    discardLabel?: string;
    confirmLabel?: string;
  }[];

  // Omit temporary expand project flows option, it will be added back in NEX-18562
  // menuItems.push({
  //   label: 'Expand Project Flows',
  //   icon: <ExpandIcon />,
  //   onClick: () => history.push(`/integrate/projects/${data.id}/expanded`)
  // });

  if (includeDetailsOption) {
    menuItems.push({
      label: 'Details',
      icon: <DetailsIcon />,
      onClick: () => onDetailClicked(data, PROJECT_DETAIL_VIEW),
    });
  }

  if (isAdmin) {
    menuItems.push({
      label: 'Manage access',
      icon: <KeyIcon />,
      onClick: () => onDetailClicked(data, PROJECT_DETAIL_VIEW_ACCESS_TAB),
    });
  }

  menuItems.push(
    {
      label: '',
      type: 'heading',
    },
    /* @TODO: Integrate with Starred API when implemented */
    !DISABLE_DEMO_FEATURES
      ? {
          label: 'Add to Starred',
          icon: <StarIcon />,
          onClick: (...args) => console.log(...args),
        }
      : null,
    /* @TODO: Restore project color functionality when we will have an API for that */
    // {
    //   label: 'Project Color',
    //   icon: <PauseIcon />,
    //   onClick: (...args) => console.log(...args)
    // },
  );

  if (isAdmin) {
    menuItems.push({
      type: 'dialog',
      label: isDeleting ? 'Deleting...' : 'Delete',
      icon: <DeleteIcon />,
      color: 'error',
      disable: isDeleting,
      dialogMessage: `Please confirm you want to delete project ${data.name}(ID: ${data.id})`,
      primaryButtonColor: 'error',
      discardLabel: 'Cancel',
      confirmLabel: 'Delete',
      onClick: handleDelete,
    });
  }

  return (
    <div className="detail-actions">
      <Menu horizontalDots={horizontalDots} items={menuItems.filter(Boolean)} inPipeline={false} />
    </div>
  );
};

export default ProjectDetailsActions;
